import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Catalogs|Exam|Item Scoring|Form|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.Exam,
	portal: 'SP',
};

type LocNameStart = 'SPCatalogsExamsItemScoringForm';
export type SPCatalogsExamsItemScoringFormLocNames =
	| `${LocNameStart}ContentInstructions`
	| `${LocNameStart}ButtonFormFieldTooltipEdit`
	| `${LocNameStart}ButtonFormFieldTooltipIgnore`
	| `${LocNameStart}ButtonFormFieldEditability`
	| `${LocNameStart}LabelWeight`
	| `${LocNameStart}LabelMaxScore`
	| `${LocNameStart}LabelCorrectAnswer`
	| `${LocNameStart}LabelUnscoredForm`
	| `${LocNameStart}LabelComment`
	| `${LocNameStart}ErrorWeightInvalid`
	| `${LocNameStart}ErrorMaximumInvalid`
	| `${LocNameStart}ButtonDeleteUnscoredForm`
	| `${LocNameStart}ButtonAddUnscoredForm`
	| `${LocNameStart}ErrorUnscoredFormsDuplicate`
	| `${LocNameStart}ContentItemsSelected`
	| `${LocNameStart}ContentNoItemsSelected`
	| `${LocNameStart}ButtonCancel`
	| `${LocNameStart}ButtonSave`
	| `${LocNameStart}ButtonDelete`
	| `${LocNameStart}NotificationDeleteConfirmation`;

export const spCatalogsExamsItemScoringFormLocs: LocDefs<SPCatalogsExamsItemScoringFormLocNames> = {
	SPCatalogsExamsItemScoringFormContentInstructions: {
		defaultValue: '',
		description: 'Instructions',
		displayPath: `${pathStart}Instructions`,
		displayType: 'Content',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.Content.FormInstructions',
		...sharedProperties,
		htmlAllowed: true,
	},
	SPCatalogsExamsItemScoringFormButtonFormFieldTooltipEdit: {
		defaultValue: 'Edit',
		description: 'Text shown in the tooltip for the edit button in the form field.',
		displayPath: `${pathStart}Tooltip Edit`,
		displayType: 'Button',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.Title.FormFieldEdit',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringFormButtonFormFieldTooltipIgnore: {
		defaultValue: 'Ignore',
		description: 'Text shown in the tooltip for the ignore button in the form field.',
		displayPath: `${pathStart}Tooltip Ignore`,
		displayType: 'Button',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.Title.FormFieldIgnore',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringFormButtonFormFieldEditability: {
		defaultValue: 'Toggle Field Editability',
		description: 'Text shown in the form field edit button for accessibility.',
		displayPath: `${pathStart}Toggle Edit`,
		displayType: 'Button',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.Button.FormFieldEditability',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringFormLabelWeight: {
		defaultValue: 'Weight',
		description: 'Field label for: Weight.',
		displayPath: `${pathStart}Weight`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldLabel.Weight',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringFormLabelMaxScore: {
		defaultValue: 'Max Score',
		description: 'Field label for: Max Score.',
		displayPath: `${pathStart}Max Score`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldLabel.Maximum',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringFormLabelCorrectAnswer: {
		defaultValue: 'Correct Answer',
		description: 'Field label for: Correct Answer.',
		displayPath: `${pathStart}Correct Answer`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldLabel.CorrectAnswer',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringFormLabelUnscoredForm: {
		defaultValue: 'Unscored Form',
		description: 'Field label for: Unscored Form.',
		displayPath: `${pathStart}Unscored Form`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldLabel.UnscoredForms',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringFormLabelComment: {
		defaultValue: 'Comment',
		description: 'Field label for: Comment.',
		displayPath: `${pathStart}Comment`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldLabel.Comment',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringFormErrorWeightInvalid: {
		defaultValue: 'Must be a number',
		description: 'Field error saying weight must be a number',
		displayPath: `${pathStart}Weight Error`,
		displayType: 'Error',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldError.WeightInvalid',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringFormErrorMaximumInvalid: {
		defaultValue: 'Must be a number',
		description: 'Field error saying max score must be a number',
		displayPath: `${pathStart}Max Score Error`,
		displayType: 'Error',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldError.MaximumInvalid',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringFormButtonDeleteUnscoredForm: {
		defaultValue: 'Delete Unscored Form',
		description: 'Delete Unscored Form button',
		displayPath: `${pathStart}Delete Unscored Form`,
		displayType: 'Button',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.Button.DeleteUnscoredForm',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringFormButtonAddUnscoredForm: {
		defaultValue: 'Add Unscored Form',
		description: 'Add Unscored Form button',
		displayPath: `${pathStart}Add Unscored Form`,
		displayType: 'Button',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.Button.AddUnscoredForm',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringFormErrorUnscoredFormsDuplicate: {
		defaultValue: 'Unscored forms must not contain duplicates',
		description: 'Error shown when unscored forms contain duplicates',
		displayPath: `${pathStart}Unscored Form Duplicates`,
		displayType: 'Error',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldError.UnscoredFormsDuplicate',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringFormContentItemsSelected: {
		defaultValue: 'Items selected:',
		description: 'Text shown when items are selected in the form.',
		displayPath: `${pathStart}Items selected`,
		displayType: 'Content',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.Content.CountLabel',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringFormContentNoItemsSelected: {
		defaultValue: 'No items selected',
		description: 'Text shown when no items are selected in the form.',
		displayPath: `${pathStart}No Items selected`,
		displayType: 'Content',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.Content.NoneSelected',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringFormButtonCancel: {
		defaultValue: 'Cancel',
		description: 'Cancel button.',
		displayPath: `${pathStart}Cancel`,
		displayType: 'Button',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.Button.FormCancel',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringFormButtonSave: {
		defaultValue: 'Apply Changes to Selected Items',
		description: 'Save button.',
		displayPath: `${pathStart}Save`,
		displayType: 'Button',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.Button.SaveSelected',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringFormButtonDelete: {
		defaultValue: 'Delete Selected Items',
		description: 'Delete button.',
		displayPath: `${pathStart}Delete`,
		displayType: 'Button',
		legacyName: 'Exam/Catalogs.Exams.ItemScoring.Button.DeleteSelected',
		...sharedProperties,
	},
	SPCatalogsExamsItemScoringFormNotificationDeleteConfirmation: {
		defaultValue: 'Are you sure you want to delete the following number of items?',
		description: 'Message shown in the delete confirmation dialog.',
		displayPath: `${pathStart}Delete Confirmation`,
		displayType: 'Notification',
		legacyName:
			'Exam.SectionCatalog/Catalogs.Exams.Sections.NotificationMessage.DeleteConfirmation',
		...sharedProperties,
	},
};

export type SPCatalogsExamsItemScoringFormLocType = LocMap<SPCatalogsExamsItemScoringFormLocNames>;
